import { useEffect, useMemo, useRef } from "react";
import { I18nextProvider } from "react-i18next";
import { FormProvider } from "react-hook-form";

import i18n, { proxyLocaleWithSalutation } from "./i18n.js";
import { ConfirmationStep } from "./ConfirmationStep.jsx";
import { CustomerStep } from "./CustomerStep.jsx";
import { ExcludedStep } from "./ExcludedStep.jsx";
import { ManualStep } from "./ManualStep.jsx";
import { OrderItemsStep } from "./OrderItemsStep.jsx";
import { StatusLookupStep } from "./StatusLookupStep.jsx";
import { useWithdrawalWizard, WithdrawalWizardProvider } from "./useWithdrawalWizard.js";
import { useWizardHistory } from "./useWizardHistory.js";

const WIZARD_SCROLL_OFFSET = 100;

export default function App({ boot, designMode }) {
  return (
    <I18nextProvider i18n={i18n}>
      <ProxyApp boot={boot} designMode={designMode} />
    </I18nextProvider>
  );
}

function ProxyApp({ boot, designMode }) {
  useEffect(() => {
    i18n.changeLanguage(proxyLocaleWithSalutation(boot.informal_salutation_enabled));
  }, [boot.informal_salutation_enabled]);

  const mode = String(boot.mode || "wizard");
  const routes = boot.routes || {};
  const statusHref = useMemo(() => {
    if (!routes.status) return "";

    return `${routes.status}${typeof window !== "undefined" ? window.location.search || "" : ""}`;
  }, [routes.status]);

  if (mode === "confirmation" && boot.withdrawal) {
    return (
      <ConfirmationStep
        additionalContent={boot.additional_content || {}}
        withdrawal={boot.withdrawal}
        enableWithdrawalStatus={Boolean(boot.enable_withdrawal_status)}
        statusPath={statusHref}
      />
    );
  }

  if (mode === "status_lookup") {
    return <StatusLookupStep routes={routes} />;
  }

  return (
    <Wizard
      routes={routes}
      additionalContent={boot.additional_content || {}}
      optionalFields={boot.optional_fields || {}}
      orderReferenceExample={String(boot.order_reference_example || "")}
      enableWithdrawalStatus={Boolean(boot.enable_withdrawal_status)}
      statusHref={statusHref}
      designMode={designMode}
    />
  );
}

function Wizard({
  routes,
  additionalContent,
  optionalFields,
  orderReferenceExample,
  enableWithdrawalStatus,
  statusHref,
  designMode,
}) {
  const wizard = useWithdrawalWizard({ routes });
  const { step, confirmationWithdrawal, orderPayload, form } = wizard;
  const wizardTopRef = useRef(null);
  const previousStepRef = useRef(step);
  const historyAwareWizard = useWizardHistory(wizard);

  useEffect(() => {
    if (previousStepRef.current === step) return;

    previousStepRef.current = step;
    if (wizardTopRef.current) {
      const top = window.scrollY + wizardTopRef.current.getBoundingClientRect().top - WIZARD_SCROLL_OFFSET;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, [step]);

  return (
    <WithdrawalWizardProvider wizard={historyAwareWizard}>
      <FormProvider {...form}>
        <div ref={wizardTopRef}>
          {step === "customer" ? (
            <CustomerStep
              additionalContent={additionalContent}
              orderReferenceExample={orderReferenceExample}
              designMode={designMode}
              enableWithdrawalStatus={enableWithdrawalStatus}
              statusPath={statusHref}
            />
          ) : null}

          {step === "confirmation" && confirmationWithdrawal ? (
            <ConfirmationStep
              additionalContent={additionalContent}
              withdrawal={confirmationWithdrawal}
              enableWithdrawalStatus={enableWithdrawalStatus}
              statusPath={statusHref}
            />
          ) : null}

          {step === "manual" ? (
            <ManualStep
              optionalFields={optionalFields}
              designMode={designMode}
            />
          ) : null}

          {step === "order" && orderPayload ? (
            <OrderItemsStep
              optionalFields={optionalFields}
              designMode={designMode}
            />
          ) : null}

          {step === "excluded" ? <ExcludedStep onBack={historyAwareWizard.goToCustomer} /> : null}
        </div>
      </FormProvider>
    </WithdrawalWizardProvider>
  );
}
